import CustomTooltip from './CustomTooltip';
import {
    CheckCircle,
    Error,
    ErrorOutline,
    InfoOutlined,
    PeopleOutline,
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
    alpha,
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    Fade,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import {
    camelCaseToLowerCase,
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
    formatCurrencyShort,
    formatTime,
} from '../../shared/utils';
import { SpaceIcons } from '../components/Experiences/SpaceCard';
import {
    getAmenitiesArray,
    getBeddingArray,
} from '../components/Experiences/Spaces/IndividualSpace';
import SpaceAttendees from '../components/Reservations/Spaces/SpaceAttendees';
import { SpaceType } from '../enums/experience';
import { Period } from '../types/experience';
import { ReservationSpaceAttendee } from '../types/reservation';

const SpaceCardContent = ({
    space,
    isSelected,
    handleCardClick,
    shouldShowSelected,
    isCommercialSpace,
    partiallyAvailable,
    disabled,
    experienceType,
    startDate,
    endDate,
    assignedAttendeesCount,
    isReservationForm,
    isExperienceForm,
    attendees,
    onEditReservation,
    handleAddSpaceForAttendees,
}: {
    space: any;
    isSelected?: boolean;
    handleCardClick?: (id: number) => void;
    shouldShowSelected: boolean;
    isCommercialSpace?: boolean;
    partiallyAvailable?: boolean;
    disabled?: boolean;
    experienceType?: string;
    startDate?: string;
    endDate?: string;
    assignedAttendeesCount?: number;
    isReservationForm: boolean;
    isExperienceForm: boolean;
    attendees?: ReservationSpaceAttendee[];
    onEditReservation?: () => void;
    handleAddSpaceForAttendees?: (space: any) => void;
}) => {
    const theme = useTheme();
    const [canAllocate, setCanAllocate] = useState(false);
    const getAttendeeLabel = (attendee: any) => {
        if (attendee?.firstName)
            return `${attendee.firstName} ${attendee.lastName ?? ''}`.trim();
        return attendee?.email || attendee?.name || 'Guest';
    };

    const getInitials = (label: string) => {
        const parts = String(label).trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
        return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
    };

    const unavailablePeriods = (space.unavailablePeriods ?? []).filter(
        (period: Period) => {
            return startDate && endDate
                ? period.from <= endDate && period.to >= startDate
                : true;
        },
    );

    const isCommercialModule =
        String(experienceType ?? '').toLowerCase() === 'commercial';

    // Commercial: parents may mark a space as "partial" (doesn't cover the full
    // stay) but still allow selection to fill one date range; `disabled` encodes
    // whether it can be added to remaining gaps. Other experience types keep the
    // stricter "must cover full window" behaviour.
    const isUnavailableForSelection =
        !isSelected &&
        (disabled || (!!partiallyAvailable && !isCommercialModule));

    const formatPeriodRange = (period: Period) => {
        const fromPart = period.startTime
            ? `${period.from} ${formatTime(period.startTime)}`
            : period.from;
        const toPart = period.endTime
            ? `${period.to} ${formatTime(period.endTime)}`
            : period.to;
        return `${fromPart} to ${toPart}`;
    };

    const tooltipText =
        `This space is not available for your entire booking due to ${unavailablePeriods
            .map(
                (period: Period) =>
                    `${period.reason === 'reserved' ? 'reservation' : camelCaseToTitleCase(period.reason)} from ${formatPeriodRange(period)}`,
            )
            .join(', ')}.` +
        (isCommercialModule
            ? ` You will be able to select additional rooms to complete your stay.`
            : '');

    const amenities = getAmenitiesArray(space as any).slice(0, 5);
    const bedding = getBeddingArray(space as any);

    const title = space?.assignedName || camelCaseToTitleCase(space.name || '');

    const spaceTypeLabel = camelCaseToTitleCase(
        space?.spaceType ?? space?.type ?? '',
    );

    const capacityLabel =
        'sleepingCapacity' in space && space.sleepingCapacity > 0
            ? `${space.sleepingCapacity} guest${space.sleepingCapacity > 1 ? 's' : ''}`
            : 'seatingCapacity' in space && space.seatingCapacity > 0
              ? `${space.seatingCapacity} people`
              : 'desk' in space && space.desk > 0
                ? `${space.desk} desk${space.desk > 1 ? 's' : ''}`
                : 'parkingCapacity' in space && space.parkingCapacity > 0
                  ? `${space.parkingCapacity} cars`
                  : null;

    const cleaningFeeAmount = space?.cleaningFee;
    const hasCleaningFee = cleaningFeeAmount > 0;

    const selectedBg = alpha(theme.palette.primary.main, 0.07);
    const selectedBorder = theme.palette.primary.main;
    const disabledBg = alpha(theme.palette.text.primary, 0.02);

    return (
        <Card
            role='option'
            aria-selected={isSelected}
            aria-disabled={isUnavailableForSelection}
            aria-label={title}
            onClick={() => {
                if (isUnavailableForSelection) return;
                handleCardClick?.(space.id);
            }}
            sx={{
                height: '100%',

                borderRadius: 2,
                border: '1px solid',
                borderColor:
                    isSelected && shouldShowSelected
                        ? selectedBorder
                        : 'custom.lightBorderColor',
                backgroundColor: isUnavailableForSelection
                    ? disabledBg
                    : isSelected && shouldShowSelected
                      ? selectedBg
                      : 'background.paper',
                boxShadow: isSelected && shouldShowSelected ? 2 : 0,
                opacity: isUnavailableForSelection ? 0.55 : 1,
                overflow: 'hidden',
                transition:
                    'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                cursor: isUnavailableForSelection ? 'not-allowed' : 'pointer',
                '&:hover':
                    isUnavailableForSelection || !shouldShowSelected
                        ? {}
                        : {
                              borderColor: isSelected
                                  ? selectedBorder
                                  : theme.palette.primary.light,
                              boxShadow: 2,
                          },
            }}
        >
            <CardActionArea
                disableRipple
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                }}
            >
                <Box sx={{ p: 1.75, flex: '1 1 auto', minHeight: 0 }}>
                    <Stack spacing={1.5}>
                        <Stack
                            direction='row'
                            alignItems='flex-start'
                            justifyContent='space-between'
                            gap={1}
                        >
                            <Stack
                                direction='row'
                                spacing={1}
                                alignItems='center'
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        flexShrink: 0,
                                        color:
                                            isSelected && shouldShowSelected
                                                ? 'primary.main'
                                                : 'text.secondary',
                                    }}
                                >
                                    {SpaceIcons[space.spaceType] || null}
                                </Box>

                                <Stack spacing={0.5}>
                                    <Stack
                                        direction='row'
                                        spacing={0.75}
                                        alignItems='center'
                                        flexWrap='wrap'
                                    >
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                fontWeight: 800,

                                                color:
                                                    isSelected &&
                                                    shouldShowSelected
                                                        ? 'primary.main'
                                                        : 'text.primary',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {title}
                                        </Typography>

                                        {partiallyAvailable && (
                                            <CustomTooltip title={tooltipText}>
                                                <Box
                                                    component='span'
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Error
                                                        color='warning'
                                                        fontSize='small'
                                                    />
                                                </Box>
                                            </CustomTooltip>
                                        )}
                                    </Stack>

                                    {capacityLabel ? (
                                        <Stack
                                            direction='row'
                                            spacing={0.75}
                                            alignItems='center'
                                        >
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Capacity: {capacityLabel}
                                            </Typography>
                                        </Stack>
                                    ) : null}
                                </Stack>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                }}
                            >
                                {!shouldShowSelected ? (
                                    <Chip
                                        label={spaceTypeLabel}
                                        size='small'
                                        sx={{
                                            borderRadius: 1.5,
                                            maxWidth: '100px',
                                            fontWeight: 600,
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.06,
                                            ),
                                            color: 'text.primary',
                                            '& .MuiChip-label': {
                                                ...theme.typography.body3,
                                                px: 1,
                                            },
                                        }}
                                    />
                                ) : null}
                                {isSelected && shouldShowSelected ? (
                                    <CheckCircle
                                        sx={{
                                            fontSize: 24,
                                            color: 'primary.main',
                                        }}
                                    />
                                ) : partiallyAvailable ? (
                                    <CustomTooltip title={tooltipText}>
                                        <Box
                                            component='span'
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <InfoOutlined
                                                sx={{
                                                    fontSize: 20,
                                                    color: 'warning.main',
                                                }}
                                            />
                                        </Box>
                                    </CustomTooltip>
                                ) : null}
                            </Box>
                        </Stack>
                        <Box
                            display='flex'
                            flexDirection='row'
                            gap={0.75}
                            alignItems='center'
                            flexWrap='wrap'
                        >
                            {shouldShowSelected ? (
                                <Chip
                                    label={spaceTypeLabel}
                                    size='small'
                                    sx={{
                                        borderRadius: 1.5,
                                        maxWidth: '100px',
                                        fontWeight: 600,
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            isSelected && shouldShowSelected
                                                ? 0.12
                                                : 0.06,
                                        ),
                                        color:
                                            isSelected && shouldShowSelected
                                                ? 'primary.main'
                                                : 'text.primary',
                                        '& .MuiChip-label': {
                                            ...theme.typography.body3,
                                            px: 1,
                                        },
                                    }}
                                />
                            ) : null}

                            {shouldShowSelected && !isCommercialSpace && (
                                <CustomTooltip
                                    title={
                                        hasCleaningFee
                                            ? `Selecting this room adds an additional cleaning fee of ${formatCurrencyShort(cleaningFeeAmount)}.`
                                            : 'No additional cleaning fee for this room. It is included in the base price.'
                                    }
                                >
                                    <Chip
                                        icon={
                                            !hasCleaningFee ? (
                                                <InfoOutlined
                                                    sx={{
                                                        fontSize: 16,
                                                        color: 'inherit',
                                                    }}
                                                />
                                            ) : undefined
                                        }
                                        label={
                                            hasCleaningFee
                                                ? `+${formatCurrencyShort(cleaningFeeAmount)} cleaning fee`
                                                : 'Included'
                                        }
                                        size='small'
                                        sx={{
                                            borderRadius: 1.5,
                                            fontWeight: 600,
                                            backgroundColor: hasCleaningFee
                                                ? alpha(
                                                      theme.palette.warning
                                                          .main,
                                                      0.12,
                                                  )
                                                : alpha(
                                                      theme.palette.success
                                                          .main,
                                                      0.12,
                                                  ),
                                            color: hasCleaningFee
                                                ? 'warning.dark'
                                                : 'success.dark',

                                            '& .MuiChip-label': {
                                                ...theme.typography.body3,
                                                px: 1,
                                            },
                                        }}
                                    />
                                </CustomTooltip>
                            )}
                        </Box>

                        {space.spaceType === SpaceType.Bedroom &&
                            !space?.sleepingCapacity &&
                            !isExperienceForm && (
                                <Stack
                                    direction='row'
                                    spacing={0.75}
                                    alignItems='flex-start'
                                    sx={{
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        backgroundColor: alpha(
                                            theme.palette.error.main,
                                            0.08,
                                        ),
                                        border: `1px solid ${alpha(
                                            theme.palette.error.main,
                                            0.22,
                                        )}`,
                                    }}
                                >
                                    <ErrorOutline
                                        sx={{
                                            fontSize: 18,
                                            color: 'error.main',
                                            flexShrink: 0,
                                            mt: 0.125,
                                        }}
                                    />
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            color: 'error.dark',
                                            fontWeight: 500,
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        This room doesn't allow guest
                                        allocation.
                                    </Typography>
                                </Stack>
                            )}

                        {(space.description ||
                            amenities.length > 0 ||
                            bedding.length > 0) && (
                            <Box
                                sx={{
                                    pt: 1.25,
                                    borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    minHeight: 76,
                                }}
                            >
                                {space.description ? (
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {space.description}
                                    </Typography>
                                ) : amenities.length > 0 ? (
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        <Box
                                            component='span'
                                            sx={{
                                                fontWeight: 800,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Amenities:{' '}
                                        </Box>
                                        {amenities
                                            .map((amenity: any) =>
                                                camelCaseToSentenceCase(
                                                    amenity,
                                                ),
                                            )
                                            .join(', ')}
                                    </Typography>
                                ) : bedding.length > 0 ? (
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        <Box
                                            component='span'
                                            sx={{
                                                fontWeight: 800,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Sleeping:{' '}
                                        </Box>
                                        {bedding
                                            .map(
                                                (item: any) =>
                                                    `${item[1]} ${camelCaseToLowerCase(item[0])}${item[1] > 1 && item[0] !== 'couch' ? 's' : item[1] > 1 && item[0] === 'couch' ? 'es' : ''}`,
                                            )
                                            .join(', ')}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Stack>
                    {space.spaceType === SpaceType.Bedroom &&
                        space.sleepingCapacity > 0 &&
                        !isExperienceForm && (
                            <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    py: 1.25,
                                    flexShrink: 0,
                                    borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                    minHeight: 60,
                                }}
                            >
                                {isReservationForm &&
                                (isSelected || canAllocate) ? (
                                    <SpaceAttendees
                                        space={space}
                                        handleAddSpaceForAttendees={
                                            handleAddSpaceForAttendees
                                        }
                                    />
                                ) : isReservationForm && shouldShowSelected ? (
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        justifyContent='center'
                                        spacing={1.25}
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                        onClick={() => {
                                            if (isUnavailableForSelection)
                                                return;
                                            handleCardClick?.(space.id);
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 36,
                                                height: 36,
                                                borderRadius: 1,
                                                flexShrink: 0,
                                                bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.08,
                                                ),
                                                color: 'primary.main',
                                            }}
                                        >
                                            <PeopleOutline
                                                sx={{ fontSize: 20 }}
                                                aria-hidden
                                            />
                                        </Box>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            select this room so you can allocate
                                            guests
                                        </Typography>
                                    </Stack>
                                ) : !shouldShowSelected ? (
                                    // View reservation: keep allocations compact & aligned.
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        justifyContent='space-between'
                                        spacing={1.5}
                                        onClick={() => {
                                            setCanAllocate(true);
                                        }}
                                        sx={{
                                            width: '100%',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Stack
                                            direction='row'
                                            spacing={1.25}
                                            alignItems='center'
                                            sx={{
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 1,
                                                    flexShrink: 0,
                                                    bgcolor: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.08,
                                                    ),
                                                    color: 'primary.main',
                                                }}
                                            >
                                                <PeopleOutline
                                                    sx={{ fontSize: 20 }}
                                                    aria-hidden
                                                />
                                            </Box>

                                            <Stack
                                                spacing={0.25}
                                                sx={{ minWidth: 0 }}
                                            >
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: 'text.primary',
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {attendees &&
                                                    attendees.length > 0
                                                        ? 'Allocated guests'
                                                        : 'No guests allocated to this room'}
                                                </Typography>
                                                <Typography
                                                    variant='body3'
                                                    sx={{
                                                        color: 'text.secondary',
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {attendees &&
                                                    attendees.length > 0
                                                        ? `${attendees.length} of ${space.sleepingCapacity} allocated`
                                                        : 'Click to allocate guests'}
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        {attendees && attendees.length > 0 ? (
                                            <AvatarGroup
                                                max={4}
                                                sx={{
                                                    '& .MuiAvatar-root': {
                                                        width: 30,
                                                        height: 30,
                                                        fontSize: 12,
                                                        borderColor:
                                                            theme.palette.custom
                                                                .lightBorderColor,
                                                    },
                                                }}
                                            >
                                                {attendees.map(
                                                    (attendee: any, i) => {
                                                        const label =
                                                            getAttendeeLabel(
                                                                attendee,
                                                            );
                                                        const src =
                                                            attendee
                                                                ?.profilePicture
                                                                ?.src;
                                                        return (
                                                            <Avatar
                                                                key={`${label}-${i}`}
                                                                src={src}
                                                                alt={label}
                                                            >
                                                                {getInitials(
                                                                    label,
                                                                )}
                                                            </Avatar>
                                                        );
                                                    },
                                                )}
                                            </AvatarGroup>
                                        ) : !isReservationForm &&
                                          onEditReservation ? (
                                            <Box
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                display='flex'
                                                flexDirection='row'
                                                gap={0.75}
                                                alignItems='center'
                                                flexWrap='wrap'
                                            >
                                                <Button
                                                    size='small'
                                                    variant='contained'
                                                    color='primary'
                                                    sx={{
                                                        textTransform: 'none',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1.5,
                                                        minWidth: 'fit-content',
                                                        fontWeight: 800,
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            boxShadow: 'none',
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        onEditReservation()
                                                    }
                                                >
                                                    Allocate
                                                </Button>
                                            </Box>
                                        ) : null}
                                    </Stack>
                                ) : attendees && attendees.length > 0 ? (
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        spacing={1.25}
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            {attendees.map(
                                                (attendee: any, index) => (
                                                    <Box
                                                        flexGrow={1}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'column',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <Box
                                                            display='flex'
                                                            justifyContent='space-between'
                                                            alignItems='flex-start'
                                                            mb={1}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 1.2,
                                                                }}
                                                            >
                                                                {attendee.profilePicture ? (
                                                                    <Avatar
                                                                        src={
                                                                            attendee
                                                                                .profilePicture
                                                                                .src
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            height: '40px',
                                                                            width: '40px',
                                                                            borderRadius:
                                                                                '50%',
                                                                        }}
                                                                    >
                                                                        <AccountCircleIcon
                                                                            sx={{
                                                                                width: '110%',
                                                                                height: '110%',
                                                                                color: 'primary.main',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        justifyContent:
                                                                            'center',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        component={
                                                                            'div'
                                                                        }
                                                                        variant='h6'
                                                                        sx={{
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '14px',
                                                                        }}
                                                                    >
                                                                        {attendee.firstName
                                                                            ? `${attendee.firstName} ${attendee.lastName}`
                                                                            : attendee.email
                                                                              ? attendee.email
                                                                              : attendee.name}
                                                                    </Typography>
                                                                    {attendee.firstName && (
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize:
                                                                                    '12px',
                                                                            }}
                                                                        >
                                                                            {attendee.email
                                                                                ? attendee.email
                                                                                : ''}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ),
                                            )}
                                        </Box>
                                    </Stack>
                                ) : null}
                            </Box>
                        )}
                </Box>
            </CardActionArea>
        </Card>
    );
};

export default SpaceCardContent;
